import React from 'react';

export type LineChartDataPoint = {
  label: string;
  value: number;
};

interface LineChartProps {
  data: LineChartDataPoint[];
  width?: number;
  height?: number;
  color?: string;
  className?: string;
}

export const LineChart: React.FC<LineChartProps> = ({
  data,
  width = 700,
  height = 240,
  color = '#2563eb',
  className,
}) => {
  if (!data || data.length === 0) {
    return <div className={className}>No chart data available.</div>;
  }

  const padding = 36;
  const values = data.map((point) => point.value);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const points = data.map((point, index) => {
    const x = padding + (index * (width - padding * 2)) / Math.max(data.length - 1, 1);
    const y = height - padding - ((point.value - min) * (height - padding * 2)) / range;
    return { ...point, x, y };
  });

  const linePath = points.map((point) => `${point.x},${point.y}`).join(' ');
  const yLabels = [max, min + range / 2, min].map((value) => Math.round(value));

  return (
    <div className={className}>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
          <defs>
            <linearGradient id="lineChartGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.35" />
              <stop offset="100%" stopColor={color} stopOpacity="0.02" />
            </linearGradient>
          </defs>
          <g>
            <line
              x1={padding}
              y1={padding}
              x2={padding}
              y2={height - padding}
              stroke="#e2e8f0"
              strokeWidth="1"
            />
            <line
              x1={padding}
              y1={height - padding}
              x2={width - padding}
              y2={height - padding}
              stroke="#e2e8f0"
              strokeWidth="1"
            />
            <path
              d={`M${linePath}`}
              fill="none"
              stroke={color}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d={`M${points[0].x},${height - padding} L${linePath} L${points[points.length - 1].x},${height - padding} Z`}
              fill="url(#lineChartGradient)"
            />
            {points.map((point) => (
              <circle key={point.label} cx={point.x} cy={point.y} r="4" fill={color} stroke="#fff" strokeWidth="2" />
            ))}
            {yLabels.map((label, index) => {
              const y = padding + (index * (height - padding * 2)) / Math.max(yLabels.length - 1, 1);
              return (
                <g key={label}>
                  <text x={padding - 10} y={y + 4} textAnchor="end" fontSize="10" fill="#64748b">
                    {label}
                  </text>
                  <line x1={padding - 4} y1={y} x2={padding} y2={y} stroke="#cbd5e1" strokeWidth="1" />
                </g>
              );
            })}
            {points.map((point) => (
              <text key={`label-${point.label}`} x={point.x} y={height - padding + 18} textAnchor="middle" fontSize="10" fill="#64748b">
                {point.label}
              </text>
            ))}
          </g>
        </svg>
      </div>
    </div>
  );
};
